// src/services/calibreClassification.ts
//
// Shared, pure helpers for deriving a calibre's generic classification and brand
// association from its manufacturer SET (primary `manufacturerId` +
// `additionalManufacturerIds` co-developers) — NOT the primary maker alone.
//
// Motivating case: "Panerai caliber OP I" is made by ETA (a generic maker) in
// partnership with Officine Panerai (a non-generic, Panerai-owned maker). Judging
// by the primary maker alone marks it generic and unscoped. Judging by the whole
// set, the non-generic co-developer wins: the calibre is non-generic and belongs
// to Panerai — so it surfaces in Panerai's AKA pick lists and brand-scoped views.
//
// The functions/ backfill re-implements the same rules (functions/src is a
// separate TS project and cannot import from src/); keep the two in sync.

import type { CustomCalibreEntry } from './firebase';
import type { MovementManufacturer } from '../types';

/** Just the manufacturer fields these helpers consult (keeps callers flexible). */
export type ManufacturerLite = Pick<
  MovementManufacturer,
  'isGeneric' | 'brandIdsManualInclude'
>;

/**
 * {@link ManufacturerLite} plus the single-answer brand — consulted only by the
 * AKA-scope helpers below, which fall back to it when a non-generic maker names
 * no brands in its allowlist.
 */
export type ManufacturerBrandLite = ManufacturerLite &
  Pick<MovementManufacturer, 'primaryBrandId'>;

/**
 * {@link ManufacturerLite} plus the exclusion list — consulted only by
 * {@link soleManufacturerIdForBrand}, which is picking a value to WRITE and so
 * has to honour the "effective brand set = union(manual, derived) - exclusions"
 * rule that the read-side scoping helpers can afford to skip.
 */
export type ManufacturerScopeLite = ManufacturerLite &
  Pick<MovementManufacturer, 'brandExclusions'>;

/** Just the calibre fields these helpers consult. */
export type CalibreManufacturerRefs = Pick<
  CustomCalibreEntry,
  'manufacturerId' | 'additionalManufacturerIds'
>;

/**
 * Every manufacturer FK on a calibre — primary first, then co-developers —
 * trimmed, non-empty, and deduped (primary can't repeat as an additional).
 */
export function calibreManufacturerIds(entry: CalibreManufacturerRefs): string[] {
  const ids: string[] = [];
  const primary = (entry.manufacturerId || '').trim();
  if (primary) ids.push(primary);
  for (const raw of entry.additionalManufacturerIds ?? []) {
    const id = (raw || '').trim();
    if (id) ids.push(id);
  }
  return [...new Set(ids)];
}

export type CalibreGenericClass = 'generic' | 'non-generic' | 'unknown';

/**
 * Classify a calibre from its manufacturer set:
 *  - 'non-generic' if ANY resolved manufacturer in the set is non-generic. Only
 *    an explicit `isGeneric === true` counts as generic — a missing/null flag is
 *    treated as non-generic (matches the "Generic" checkbox default: unchecked =
 *    non-generic). So a non-generic (or unclassified) co-developer always wins.
 *  - 'generic' if EVERY resolved maker is explicitly generic (`isGeneric === true`).
 *  - 'unknown' only if no manufacturer resolves at all.
 */
export function classifyCalibreGeneric<M extends ManufacturerLite>(
  entry: CalibreManufacturerRefs,
  manufacturerById: Map<string, M>,
): CalibreGenericClass {
  let sawGeneric = false;
  for (const id of calibreManufacturerIds(entry)) {
    const mfr = manufacturerById.get(id);
    if (!mfr) continue;
    // Missing/null isGeneric defaults to non-generic — only `true` is generic.
    if (mfr.isGeneric !== true) return 'non-generic';
    sawGeneric = true;
  }
  return sawGeneric ? 'generic' : 'unknown';
}

/**
 * Brand ids a calibre is tied to via its manufacturer set. A manufacturer is
 * tied to a brand through its admin-curated `brandIdsManualInclude` allowlist,
 * or — for an in-house maker whose manufacturer doc id IS a brand slug — through
 * its own id. Only NON-generic makers contribute: a generic maker (ETA) doesn't
 * make a calibre "belong" to a brand, but its non-generic co-developer does.
 * Only an explicit `isGeneric === true` is treated as generic (skipped); a
 * missing/null flag counts as non-generic and contributes.
 */
export function calibreBrandIdsViaManufacturers<M extends ManufacturerLite>(
  entry: CalibreManufacturerRefs,
  manufacturerById: Map<string, M>,
  brandIdExists?: (brandId: string) => boolean,
): Set<string> {
  const out = new Set<string>();
  for (const id of calibreManufacturerIds(entry)) {
    const mfr = manufacturerById.get(id);
    // Skip only definitively-generic makers — missing/null counts as non-generic.
    if (!mfr || mfr.isGeneric === true) continue;
    if (brandIdExists?.(id)) out.add(id);
    for (const raw of mfr.brandIdsManualInclude ?? []) {
      const bid = (raw || '').trim();
      if (bid) out.add(bid);
    }
  }
  return out;
}

/**
 * Brands an ALIAS (AKA) on this calibre may sensibly be scoped to — i.e. the
 * option list its brand picker should offer.
 *
 * `null` means "don't narrow": the caller shows the whole brand catalogue. That
 * is deliberately distinct from an empty array, which never happens here — a
 * scope we can't populate falls through to `null` rather than to a picker with
 * nothing in it.
 *
 * The rules, in order:
 *  - No manufacturer on the calibre (or none of its FKs resolve) → `null`.
 *    An unclassified calibre gets the full catalogue, as today.
 *  - Every resolved maker is explicitly generic (ETA, Sellita…) → `null`. A
 *    generic movement's aliases are not a brand's to claim.
 *  - Otherwise (at least one non-generic maker): the brands that maker is
 *    associated with — {@link calibreBrandIdsViaManufacturers}, i.e. the
 *    `brandIdsManualInclude` allowlist plus the in-house case where the
 *    manufacturer doc id IS a brand slug.
 *  - Non-generic but associated with no brand at all → its `primaryBrandId`,
 *    as a single-element list, if one is configured.
 *  - Non-generic with neither → `null`. Nothing to narrow to, so don't.
 *
 * `brandIdsDerivedFromRefs` is deliberately NOT consulted: it is auto-populated
 * from ref usage and would readmit most of the catalogue for any maker whose
 * movements are widely used, which is exactly what this narrowing exists to
 * stop. Curated association only.
 */
export function akaBrandScopeIds<M extends ManufacturerBrandLite>(
  entry: CalibreManufacturerRefs,
  manufacturerById: Map<string, M>,
  brandIdExists?: (brandId: string) => boolean,
): string[] | null {
  if (classifyCalibreGeneric(entry, manufacturerById) !== 'non-generic') {
    return null;
  }
  const associated = calibreBrandIdsViaManufacturers(
    entry,
    manufacturerById,
    brandIdExists,
  );
  if (associated.size > 0) return [...associated];

  // No curated association — fall back to each non-generic maker's default brand.
  const fallback = new Set<string>();
  for (const id of calibreManufacturerIds(entry)) {
    const mfr = manufacturerById.get(id);
    if (!mfr || mfr.isGeneric === true) continue;
    const primary = (mfr.primaryBrandId || '').trim();
    if (primary) fallback.add(primary);
  }
  return fallback.size > 0 ? [...fallback] : null;
}

/**
 * The brand a NEW alias row should be pre-filled with, or `undefined` to leave
 * it unscoped (the behaviour before this existed).
 *
 * Only fills when the answer is unambiguous: a scope of exactly one brand, or a
 * multi-brand scope where a non-generic maker names one of them as its
 * `primaryBrandId`. A two-brand scope with no primary stays blank rather than
 * guessing — the picker is right there.
 */
export function defaultAkaBrandId<M extends ManufacturerBrandLite>(
  entry: CalibreManufacturerRefs,
  manufacturerById: Map<string, M>,
  brandIdExists?: (brandId: string) => boolean,
): string | undefined {
  const scope = akaBrandScopeIds(entry, manufacturerById, brandIdExists);
  if (!scope || scope.length === 0) return undefined;
  if (scope.length === 1) return scope[0];
  for (const id of calibreManufacturerIds(entry)) {
    const mfr = manufacturerById.get(id);
    if (!mfr || mfr.isGeneric === true) continue;
    const primary = (mfr.primaryBrandId || '').trim();
    if (primary && scope.includes(primary)) return primary;
  }
  return undefined;
}

/**
 * The inverse lookup: every manufacturer id that would tie a calibre to this
 * brand under {@link calibreTiedToBrandViaManufacturers}.
 *
 * Same two rules, read backwards — a manufacturer counts if its own doc id IS
 * the brand slug (the in-house case) or its `brandIdsManualInclude` allowlist
 * names the brand. Definitively-generic makers are skipped, exactly as they are
 * in the forward check, so ETA never drags every ETA-powered calibre into a
 * brand's pick list.
 *
 * Exists so a brand-scoped calibre pick list can be expressed as an Algolia
 * filter (`allManufacturerIds:<id>`) instead of loading the whole collection and
 * testing each document. That filter is a superset — the index inflates the set
 * with each maker's heritage ancestors — so callers still run the forward check
 * on the hydrated documents.
 */
export function brandScopeManufacturerIds<
  M extends ManufacturerLite & { id: string },
>(brandId: string, manufacturers: M[]): string[] {
  const target = (brandId || '').trim();
  if (!target) return [];
  // Seeded with the brand slug itself, not only with a manufacturer that has
  // it as a doc id: the forward check matches on the raw FK, so a calibre
  // pointing at a manufacturer doc that no longer exists still ties. Filtering
  // on an id nothing carries costs nothing.
  const ids = new Set<string>([target]);
  for (const mfr of manufacturers) {
    if (mfr.id === target) {
      ids.add(mfr.id);
      continue;
    }
    // Skip only definitively-generic makers — missing/null counts as non-generic.
    if (mfr.isGeneric === true) continue;
    if (
      (mfr.brandIdsManualInclude ?? []).some((b) => (b || '').trim() === target)
    ) {
      ids.add(mfr.id);
    }
  }
  return [...ids];
}

/**
 * The ONE movement manufacturer a brand is linked to, or `undefined` when the
 * answer isn't unambiguous.
 *
 * For the write path, where {@link brandScopeManufacturerIds} can't be used.
 * That function builds a deliberately generous SUPERSET for an Algolia filter —
 * it seeds the brand slug itself whether or not a manufacturer doc carries that
 * id, because filtering on an id nothing carries is free. Writing an FK is not
 * free: `manufacturerId` is what watchlock derives `manufacturerIdAncestors`
 * from, so a value pointing at a document that doesn't exist is worse than no
 * value at all. Every id returned here comes off a real catalogue entry.
 *
 * A manufacturer counts as linked when either
 *  - its own doc id IS the brand slug (the in-house case: Officine Panerai),
 *  - its `brandIdsManualInclude` allowlist names the brand, or
 *  - the brand's own `manufacturerIdsManualInclude` override names it.
 *
 * and is then dropped when its `brandExclusions` names the brand, or the
 * brand's `manufacturerIdsManualExclude` names it. `brandIdsDerivedFromRefs` is
 * deliberately NOT consulted — a link inferred from ref usage is a rollup, not
 * a curation decision, and every existing brand-scoping surface ignores it too.
 *
 * Definitively-generic makers (ETA, Sellita, …) are skipped, matching the
 * forward and inverse checks either side of this one. A brand whose only linked
 * maker is generic therefore gets `undefined`: the brand is the movement's
 * *user*, not its maker, and stamping ETA onto a brand's in-house stub would be
 * the wrong kind of wrong.
 *
 * Returns a value ONLY when exactly one manufacturer survives. Zero and two-or-
 * more are the same answer — we don't know — and the admin fills the field in
 * the editor rather than getting a coin flip written for them.
 */
export function soleManufacturerIdForBrand<
  M extends ManufacturerScopeLite & { id: string },
>(
  brandId: string | undefined,
  manufacturers: M[],
  brandOverrides?: { include?: string[]; exclude?: string[] },
): string | undefined {
  const target = (brandId || '').trim();
  if (!target) return undefined;
  const trimmedSet = (list?: string[]) =>
    new Set((list ?? []).map((v) => (v || '').trim()).filter(Boolean));
  const brandInclude = trimmedSet(brandOverrides?.include);
  const brandExclude = trimmedSet(brandOverrides?.exclude);

  const linked = new Set<string>();
  for (const mfr of manufacturers) {
    const id = (mfr.id || '').trim();
    if (!id) continue;
    if (brandExclude.has(id)) continue;
    if (mfr.isGeneric === true) continue;
    if (trimmedSet(mfr.brandExclusions).has(target)) continue;
    const isLinked =
      id === target ||
      trimmedSet(mfr.brandIdsManualInclude).has(target) ||
      brandInclude.has(id);
    if (isLinked) linked.add(id);
  }
  return linked.size === 1 ? [...linked][0] : undefined;
}

/** The calibre fields the brand-visibility helpers below consult. */
export type CalibreBrandRefs = Pick<
  CustomCalibreEntry,
  'brandId' | 'brandName' | 'aliasDetails'
>;

/**
 * Every brand under which any name this calibre answers to should surface:
 * the primary `brandId` plus the `brandId` on each brand-scoped alias.
 *
 * A calibre is not owned by one brand. "NOMOS Alpha" is made by D. Malchert and
 * carries an `Alpha` alias scoped to Nomos Glashütte; a Sellita SW200 answers to
 * a different house name under dozens of brands. Collapsing that to the single
 * primary `brandId` is what made a brand-scoped alias unreachable — the calibre
 * was discarded before its aliases were ever read.
 *
 * Empty means nothing scopes this calibre at all (the generic-movement case),
 * which is treated as universal rather than as "belongs to no one".
 */
export function matchableBrandIds(entry: CalibreBrandRefs): string[] {
  const ids = new Set<string>();
  const primary = (entry.brandId || '').trim();
  if (primary) ids.add(primary);
  for (const detail of entry.aliasDetails ?? []) {
    const id = (detail?.brandId || '').trim();
    if (id) ids.add(id);
  }
  return [...ids];
}

/**
 * True when a calibre's ALIASES are in scope for an import running under
 * `brandId` / `brandName` — i.e. whether its house nicknames are on offer here.
 *
 * This is a COARSE gate over the alias path only. It decides which calibres are
 * worth considering by anything other than their own name; the authoritative
 * per-name rule is `calibreMatchKeysForBrand`, which still runs afterwards and
 * still refuses an alias scoped to another brand.
 *
 * It deliberately does NOT gate canonical names. A calibre's own name is
 * universal — a Bulova reference reading "Sellita Calibre SW200" is naming the
 * base movement it is fitted with, and Sellita owning that calibre is no reason
 * to hide it from the import. `buildCalibreMatchIndex` therefore indexes every
 * entry's canonical name past this gate; only aliases pass through it.
 *
 * Strictly wider than the primary-brandId-only filter it replaces: every calibre
 * the old gate admitted is still admitted.
 *
 * OWNERSHIP COMES FROM THE PRIMARY COLUMNS ALONE — `brandId` / `brandName`. An
 * alias scope only ever ADDS a brand this calibre is visible under; it can never
 * withdraw visibility from another. Reading ownership off the full matchable set
 * instead is what broke 177 unowned calibres: a generic movement carrying one
 * brand-scoped AKA stopped being universal, and its own unscoped AKAs became
 * unreachable from every other brand.
 */
export function calibreVisibleToBrand(
  entry: CalibreBrandRefs,
  brandId?: string,
  brandName?: string,
): boolean {
  const matchable = matchableBrandIds(entry);
  if (matchable.length > 0) {
    // No brand context at all → the id filter can't express anything.
    if (!brandId) return true;
    if (matchable.includes(brandId)) return true;
    // Fall through to the legacy free-text column rather than rejecting here,
    // so a calibre carrying only a `brandName` for this brand still gets in.
  }
  const legacy = (entry.brandName || '').trim();
  // No legacy column to compare against, so the answer rests on ownership:
  // unowned is universal, owned-by-someone-else is out.
  if (!legacy) return !(entry.brandId || '').trim();
  return legacy.toLowerCase() === (brandName || '').trim().toLowerCase();
}

/**
 * The `brandId` a newly created calibre is scoped to.
 *
 * The surrounding context — the import wizard's brand, the reference editor's
 * brand, the calibre editor's brand picker — is the ONLY source. An AI-proposed
 * brand slug is never accepted, however confident the model sounds, because the
 * model cannot know how {@link deriveBrandSlug} keys a watchBrands document:
 * accents are STRIPPED rather than transliterated, so "Nomos Glashütte" is
 * `nomos-glashtte` and every plausible guess (`nomos-glashutte`,
 * `nomos-glashuette`) names a document that does not exist. The enrichCalibre
 * prompt's own worked example, "a-lange-sohne", is likewise wrong — the real
 * slug is `a-lange-shne`.
 *
 * A brandId that resolves to nothing is strictly worse than none at all: the
 * brand chip still renders (it falls back to the free-text `brandName`), so the
 * calibre LOOKS correctly scoped while silently dropping out of every
 * brand-filtered index it belongs in — including the bulk importer's own match
 * list, which hard-filters on `entry.brandId === brandId`. That is the failure
 * this helper exists to make unrepresentable.
 *
 * Generic movements stay unscoped: for an ETA 2824-2 the surrounding brand is
 * the movement's USER, not its owner.
 */
export function acceptedCalibreBrandId(opts: {
  /** The brand the surrounding UI is already scoped to. */
  contextBrandId?: string;
  isGeneric: boolean;
}): string | undefined {
  if (opts.isGeneric) return undefined;
  return (opts.contextBrandId || '').trim() || undefined;
}

/**
 * True when a calibre is tied to a specific brand via its manufacturer set.
 * Used by the AKA pick list to surface a co-developed calibre (e.g. OP I) under
 * its co-developer's brand (Panerai) even when the calibre carries no `brandId`
 * of its own. Checks both the `brandIdsManualInclude` allowlist and the
 * in-house case where a manufacturer doc id equals the brand slug.
 */
export function calibreTiedToBrandViaManufacturers<M extends ManufacturerLite>(
  entry: CalibreManufacturerRefs,
  brandId: string,
  manufacturerById: Map<string, M>,
): boolean {
  const target = (brandId || '').trim();
  if (!target) return false;
  for (const id of calibreManufacturerIds(entry)) {
    if (id === target) return true;
    const mfr = manufacturerById.get(id);
    // Skip only definitively-generic makers — missing/null counts as non-generic.
    if (!mfr || mfr.isGeneric === true) continue;
    if (
      (mfr.brandIdsManualInclude ?? []).some((b) => (b || '').trim() === target)
    ) {
      return true;
    }
  }
  return false;
}
